import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, SecurityContext, SimpleChanges, ViewChild, inject } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { TruncateTextService } from "../../services/truncate.text.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

/**
 * Just a simple counter so we can generate "unique" ids for each instance
 */
let counter = 0;

@Component({
    selector: "truncate-text",
    templateUrl: "./truncate.text.tmpl.html",
    styleUrls: [
        "./truncate.text.component..scss",
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TruncateTextComponent
    implements AfterViewInit, OnChanges, OnDestroy
{
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly domSanitizer = inject(DomSanitizer);
    private readonly $truncateTextService = inject(TruncateTextService);

    @Input() readonly allowHtml: boolean;
    @Input("mainText") readonly rawContent: string;
    @Input() readonly multiLine: boolean;

    @ViewChild("contentHost") private contentElement: ElementRef<HTMLSpanElement>;

    readonly bindingId: string;

    showTooltip: boolean;

    sanitizedContent: string;

    constructor()
    {
        this.bindingId = `truncated-text_${counter++}`;
        this.$truncateTextService.onResize.pipe(takeUntilDestroyed())
            .subscribe(() =>
            {
                this.showTooltip = this.isTruncated();
                this.changeDetectorRef.markForCheck();
            });
    }

    ngOnChanges(changes: SimpleChanges): void
    {
        this.sanitizedContent = this.domSanitizer.sanitize(SecurityContext.HTML, this.rawContent);
        if (!changes.rawContent.firstChange)
        {
            requestAnimationFrame(() =>
            {
                this.showTooltip = this.isTruncated();
                this.changeDetectorRef.markForCheck();
            });
        }
    }

    get content(): string
    {
        return this.sanitizedContent;
    }

    get targetId(): string
    {
        return `#${this.bindingId}`;
    }

    get class(): Record<string, boolean>
    {
        /* eslint-disable @typescript-eslint/naming-convention */
        return {
            "truncated": true,
            "no-wrap": !this.multiLine
        };
        /* eslint-enable @typescript-eslint/naming-convention */
    }

    ngAfterViewInit(): void
    {
        setTimeout(() =>
        {
            this.showTooltip = this.isTruncated();
            this.changeDetectorRef.markForCheck();
        });
    }

    ngOnDestroy(): void
    {
        this.contentElement = undefined;
    }

    private isTruncated(): boolean
    {
        const contentElement = this.contentElement?.nativeElement;
        if (!contentElement)
        {
            return false;
        }

        if (!this.rawContent)
        {
            // TODO - what is adding this attribute? it's not in the template....
            contentElement.removeAttribute("aria-describedby");
            return false;
        }

        const expectedContent = this.allowHtml
            ? this.content
            : this.rawContent;
        if (contentElement.innerHTML?.toLowerCase() === expectedContent?.toString()?.toLowerCase())
        {
            const truncate = contentElement.scrollWidth > contentElement.clientWidth;
            if (!truncate)
            {
                contentElement.removeAttribute("aria-describedby");
            }
            return truncate;
        }

        contentElement.removeAttribute("aria-describedby");
        return false;

    }
}
